/**
 * Cursorul de celula — masina de stari a grilei.
 *
 * Grila este UN SINGUR tab stop (roving `tabindex`), ca `Tab` sa nu plimbe utilizatorul prin
 * zeci de mii de celule, ci sa treaca la urmatoarea REGIUNE: filtre -> scrubber -> grila ->
 * drawer -> bara de actiuni. In interiorul grilei se navigheaza cu sagetile, exact ca in Excel.
 *
 * Ce tine hook-ul:
 *   - poziția cursorului si eventuala ancora de interval (Shift + sageti);
 *   - starea de editare, cu textul in lucru — la eroare valoarea RAMANE tastata, nu se pierde;
 *   - selectia de randuri, pentru actiunile in bloc;
 *   - focusul DOM, inclusiv aducerea celulei in zona vizibila SIGURA (nu doar "in view"):
 *     headerul si bara de actiuni sunt sticky, deci `scrollIntoView` singur ar aseza celula
 *     exact sub ele (WCAG 2.2 SC 2.4.11 Focus Not Obscured).
 *
 * Ce NU tine: cum arata celulele si de unde vin datele. Hook-ul primeste callback-uri.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { StatusCode } from '@evomag/core/status';
import { dinTsv, extrageZona, laTsv, scrieInClipboard } from './clipboard.ts';
import { mapeazaTasta, type ActiuneGrila } from './tastatura.ts';

export interface Pozitie {
  rand: number;
  col: number;
}

export interface Interval {
  randStart: number;
  randStop: number;
  colStart: number;
  colStop: number;
}

export interface StareEditare {
  rand: number;
  col: number;
  /** Textul in lucru. La respingerea salvarii rămâne aici, ca sa nu se piarda ce s-a tastat. */
  text: string;
  /** Primul caracter a venit din type-to-fill? Atunci cursorul din input sta la final. */
  dinTastare: boolean;
}

export interface OptiuniCursor {
  numarRanduri: number;
  numarColoane: number;
  /** Id-urile randurilor, pentru selectie si pentru actiuni in bloc. */
  idRanduri: readonly string[];

  /** Celula accepta editare? Implicit: nu. */
  esteEditabila?: (rand: number, col: number) => boolean;
  /** Celula e numerica? Doar acolo o cifra tastata incepe editarea. Implicit: ca `esteEditabila`. */
  esteNumerica?: (rand: number, col: number) => boolean;
  /** Textul brut al celulei, pentru clipboard si pentru `Ctrl+D`. */
  citesteCelula?: (rand: number, col: number) => string;

  /** Salveaza. Poate arunca: atunci editarea rămâne deschisa cu textul tastat. */
  laSalvare?: (rand: number, col: number, text: string) => void | Promise<void>;
  laStergere?: (rand: number, col: number) => void | Promise<void>;
  laLipire?: (la: Pozitie, matrice: readonly (readonly string[])[]) => void | Promise<void>;

  laCiclareStare?: (rand: number, directie: 1 | -1) => void;
  laSetareStare?: (rand: number, cod: StatusCode) => void;

  laDeschidere?: (ce: 'facturi' | 'comentariu' | 'atasamente' | 'drawer', rand: number) => void;
  laAmanare?: (idRanduri: readonly string[]) => void;
  laNavigareZi?: (delta: number) => void;
  laNavigareSaptamana?: (delta: number) => void;

  /** Cate randuri sare `PageUp`/`PageDown`. Implicit 12. */
  randuriPePagina?: number;
  /** Inaltimea zonelor sticky (header sus, bara de actiuni jos), in pixeli. */
  zonaSigura?: { sus: number; jos: number };

  /**
   * Selectie CONTROLATA din exterior. Ecranul zilnic o tine in URL (`?sel=`), ca sa poata fi
   * trimisa prin link — deci acolo hook-ul nu are voie sa aiba propria copie, altfel cele doua
   * ar diverge la primul back/forward din browser. Cand lipsesc, hook-ul isi tine singur starea.
   */
  selectieControlata?: readonly string[];
  laSchimbareSelectie?: (idRanduri: readonly string[]) => void;
}

export interface RezultatCursor {
  cursor: Pozitie;
  editare: StareEditare | null;
  /** Intervalul selectat cu Shift + sageti, sau `null`. */
  interval: Interval | null;
  randuriSelectate: ReadonlySet<string>;
  /** Ultima eroare de salvare, pentru chip-ul `Nesalvat`. */
  eroare: { rand: number; col: number; mesaj: string } | null;

  proprietatiGrila: {
    role: 'grid';
    onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => void;
    onPaste: (e: React.ClipboardEvent<HTMLElement>) => void;
  };
  /** Se pune pe fiecare celula. Randul si coloana sunt 0-based. */
  proprietatiCelula: (rand: number, col: number) => ProprietatiCelula;

  mutaLa: (rand: number, col: number) => void;
  incepeEditarea: (rand: number, col: number, caracter?: string | null) => void;
  schimbaTextul: (text: string) => void;
  salveaza: (apoi?: 'jos' | 'sus' | 'dreapta' | 'stanga' | 'ramai') => Promise<void>;
  renunta: () => void;
  comutaSelectia: (id: string) => void;
  selecteazaTot: () => void;
  golesteSelectia: () => void;
}

export interface ProprietatiCelula {
  ref: (el: HTMLElement | null) => void;
  tabIndex: number;
  'data-cursor': boolean | undefined;
  'data-in-interval': boolean | undefined;
  'aria-colindex': number;
  onMouseDown: (e: React.MouseEvent) => void;
}

const IMPLICIT_RANDURI_PE_PAGINA = 12;

export function useCellCursor(optiuni: OptiuniCursor): RezultatCursor {
  const {
    numarRanduri,
    numarColoane,
    idRanduri,
    esteEditabila = () => false,
    citesteCelula = () => '',
    randuriPePagina = IMPLICIT_RANDURI_PE_PAGINA,
    zonaSigura = { sus: 0, jos: 0 },
  } = optiuni;
  const esteNumerica = optiuni.esteNumerica ?? esteEditabila;

  const [cursor, setCursor] = useState<Pozitie>({ rand: 0, col: 0 });
  const [ancora, setAncora] = useState<Pozitie | null>(null);
  const [editare, setEditare] = useState<StareEditare | null>(null);
  const [selectieInterna, setSelectieInterna] = useState<ReadonlySet<string>>(new Set());
  const [eroare, setEroare] = useState<RezultatCursor['eroare']>(null);

  const controlata = optiuni.selectieControlata !== undefined;
  const randuriSelectate = useMemo<ReadonlySet<string>>(
    () => (controlata ? new Set(optiuni.selectieControlata) : selectieInterna),
    [controlata, optiuni.selectieControlata, selectieInterna],
  );

  const aplicaSelectia = useCallback(
    (urmatoare: ReadonlySet<string>) => {
      if (controlata) optiuni.laSchimbareSelectie?.([...urmatoare]);
      else setSelectieInterna(urmatoare);
    },
    [controlata, optiuni],
  );

  const celule = useRef(new Map<string, HTMLElement>());
  /** Cursorul curent, citit din callback-uri fara sa le re-creeze la fiecare miscare. */
  const cursorRef = useRef(cursor);
  cursorRef.current = cursor;
  const editareRef = useRef(editare);
  editareRef.current = editare;

  const cheie = (rand: number, col: number): string => `${rand}:${col}`;

  const limiteaza = useCallback(
    (p: Pozitie): Pozitie => ({
      rand: Math.max(0, Math.min(numarRanduri - 1, p.rand)),
      col: Math.max(0, Math.min(numarColoane - 1, p.col)),
    }),
    [numarRanduri, numarColoane],
  );

  /**
   * Aduce celula in zona vizibila SIGURA. `scrollIntoView({block:'nearest'})` ar aseza-o
   * exact sub headerul sticky; de aceea calculam manual fata de insetul de sus si de jos.
   */
  const aduceInVizibil = useCallback(
    (rand: number, col: number) => {
      const el = celule.current.get(cheie(rand, col));
      if (!el) return;
      const container = el.closest<HTMLElement>('[data-grid-scroll]');
      if (!container) {
        el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        return;
      }
      const c = container.getBoundingClientRect();
      const e = el.getBoundingClientRect();
      const susSigur = c.top + zonaSigura.sus;
      const josSigur = c.bottom - zonaSigura.jos;
      if (e.top < susSigur) container.scrollTop -= susSigur - e.top;
      else if (e.bottom > josSigur) container.scrollTop += e.bottom - josSigur;
      if (e.left < c.left) container.scrollLeft -= c.left - e.left;
      else if (e.right > c.right) container.scrollLeft += e.right - c.right;
    },
    [zonaSigura.sus, zonaSigura.jos],
  );

  const focuseaza = useCallback(
    (rand: number, col: number) => {
      const el = celule.current.get(cheie(rand, col));
      // Focusul se muta doar daca nu suntem in editare: acolo focusul aparține inputului.
      if (el && editareRef.current === null) el.focus({ preventScroll: true });
      aduceInVizibil(rand, col);
    },
    [aduceInVizibil],
  );

  const mutaLa = useCallback(
    (rand: number, col: number) => {
      const p = limiteaza({ rand, col });
      setCursor(p);
      setAncora(null);
      queueMicrotask(() => focuseaza(p.rand, p.col));
    },
    [limiteaza, focuseaza],
  );

  const incepeEditarea = useCallback(
    (rand: number, col: number, caracter: string | null = null) => {
      if (!esteEditabila(rand, col)) return;
      setEroare(null);
      setEditare({
        rand,
        col,
        text: caracter ?? citesteCelula(rand, col),
        dinTastare: caracter !== null,
      });
    },
    [esteEditabila, citesteCelula],
  );

  const schimbaTextul = useCallback((text: string) => {
    setEditare((e) => (e === null ? null : { ...e, text }));
  }, []);

  const renunta = useCallback(() => {
    setEditare(null);
    setEroare(null);
    const { rand, col } = cursorRef.current;
    queueMicrotask(() => focuseaza(rand, col));
  }, [focuseaza]);

  const salveaza = useCallback(
    async (apoi: 'jos' | 'sus' | 'dreapta' | 'stanga' | 'ramai' = 'jos') => {
      const e = editareRef.current;
      if (e === null) return;
      try {
        await optiuni.laSalvare?.(e.rand, e.col, e.text);
        setEditare(null);
        setEroare(null);
        const delta =
          apoi === 'jos'
            ? { r: 1, c: 0 }
            : apoi === 'sus'
              ? { r: -1, c: 0 }
              : apoi === 'dreapta'
                ? { r: 0, c: 1 }
                : apoi === 'stanga'
                  ? { r: 0, c: -1 }
                  : { r: 0, c: 0 };
        const p = limiteaza({ rand: e.rand + delta.r, col: e.col + delta.c });
        setCursor(p);
        queueMicrotask(() => focuseaza(p.rand, p.col));
      } catch (err) {
        // Valoarea RAMANE tastata: o pierdere de text la eroare de retea ar costa o dimineata.
        setEroare({
          rand: e.rand,
          col: e.col,
          mesaj: err instanceof Error ? err.message : 'Nu s-a putut salva.',
        });
      }
    },
    [optiuni, limiteaza, focuseaza],
  );

  const comutaSelectia = useCallback(
    (id: string) => {
      const next = new Set(randuriSelectate);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      aplicaSelectia(next);
    },
    [randuriSelectate, aplicaSelectia],
  );

  const selecteazaTot = useCallback(() => {
    // A doua apasare de `Ctrl+A` deselecteaza tot — altfel n-ar exista nicio cale rapida inapoi.
    aplicaSelectia(randuriSelectate.size === idRanduri.length ? new Set() : new Set(idRanduri));
  }, [randuriSelectate, idRanduri, aplicaSelectia]);

  const golesteSelectia = useCallback(() => aplicaSelectia(new Set()), [aplicaSelectia]);

  const interval = useMemo<Interval | null>(() => {
    if (ancora === null) return null;
    return {
      randStart: Math.min(ancora.rand, cursor.rand),
      randStop: Math.max(ancora.rand, cursor.rand),
      colStart: Math.min(ancora.col, cursor.col),
      colStop: Math.max(ancora.col, cursor.col),
    };
  }, [ancora, cursor]);

  // -------------------------------------------------------------------------
  // Executarea actiunilor
  // -------------------------------------------------------------------------

  const executa = useCallback(
    async (actiune: ActiuneGrila): Promise<void> => {
      const { rand, col } = cursorRef.current;

      switch (actiune.tip) {
        case 'muta': {
          const p = limiteaza({ rand: rand + actiune.dRand, col: col + actiune.dCol });
          if (actiune.extinde) {
            setAncora((a) => a ?? { rand, col });
          } else {
            setAncora(null);
          }
          setCursor(p);
          queueMicrotask(() => focuseaza(p.rand, p.col));
          return;
        }
        case 'muta-la': {
          const r =
            actiune.rand === 'prima' ? 0 : actiune.rand === 'ultima' ? numarRanduri - 1 : rand;
          const c =
            actiune.col === 'prima' ? 0 : actiune.col === 'ultima' ? numarColoane - 1 : col;
          mutaLa(r, c);
          return;
        }
        case 'pagina': {
          mutaLa(rand + actiune.directie * randuriPePagina, col);
          return;
        }
        case 'incepe-editare':
          incepeEditarea(rand, col, actiune.caracter);
          return;
        case 'commit':
          await salveaza(actiune.apoi);
          return;
        case 'renunta':
          if (editareRef.current !== null) renunta();
          else {
            setAncora(null);
            golesteSelectia();
          }
          return;
        case 'sterge':
          await optiuni.laStergere?.(rand, col);
          return;
        case 'cicleaza-stare':
          optiuni.laCiclareStare?.(rand, actiune.directie);
          return;
        case 'seteaza-stare':
          optiuni.laSetareStare?.(rand, actiune.cod);
          return;
        case 'comuta-selectie': {
          const id = idRanduri[rand];
          if (id !== undefined) comutaSelectia(id);
          return;
        }
        case 'selecteaza-tot':
          selecteazaTot();
          return;
        case 'fill-down': {
          // `Ctrl+D` copiaza valoarea de deasupra — pe intreg intervalul daca exista unul.
          const tinta = interval ?? {
            randStart: rand,
            randStop: rand,
            colStart: col,
            colStop: col,
          };
          for (let c = tinta.colStart; c <= tinta.colStop; c++) {
            const sursa = tinta.randStart - 1;
            if (sursa < 0) continue;
            const text = citesteCelula(sursa, c);
            for (let r = tinta.randStart; r <= tinta.randStop; r++) {
              if (esteEditabila(r, c)) await optiuni.laSalvare?.(r, c, text);
            }
          }
          return;
        }
        case 'copiaza': {
          const zona = interval ?? {
            randStart: rand,
            randStop: rand,
            colStart: col,
            colStop: col,
          };
          await scrieInClipboard(laTsv(extrageZona(zona, citesteCelula)));
          return;
        }
        case 'lipeste':
          // Lipirea reala vine din evenimentul `paste`, care aduce si conținutul.
          return;
        case 'deschide':
          optiuni.laDeschidere?.(actiune.ce, rand);
          return;
        case 'amana': {
          const ids =
            randuriSelectate.size > 0
              ? [...randuriSelectate]
              : [idRanduri[rand]].filter((x): x is string => x !== undefined);
          optiuni.laAmanare?.(ids);
          return;
        }
        case 'zi':
          optiuni.laNavigareZi?.(actiune.delta);
          return;
        case 'saptamana':
          optiuni.laNavigareSaptamana?.(actiune.delta);
          return;
      }
    },
    [
      limiteaza,
      focuseaza,
      mutaLa,
      numarRanduri,
      numarColoane,
      randuriPePagina,
      incepeEditarea,
      salveaza,
      renunta,
      golesteSelectia,
      optiuni,
      idRanduri,
      comutaSelectia,
      selecteazaTot,
      interval,
      citesteCelula,
      esteEditabila,
      randuriSelectate,
    ],
  );

  const laTasta = useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      const { rand, col } = cursorRef.current;
      const actiune = mapeazaTasta(e, {
        seEditeaza: editareRef.current !== null,
        editabila: esteEditabila(rand, col),
        numerica: esteNumerica(rand, col),
      });
      if (actiune === null) return;
      e.preventDefault();
      e.stopPropagation();
      void executa(actiune);
    },
    [esteEditabila, esteNumerica, executa],
  );

  const laLipire = useCallback(
    (e: React.ClipboardEvent<HTMLElement>) => {
      if (editareRef.current !== null) return; // in editare, lipirea aparține inputului
      const text = e.clipboardData.getData('text/plain');
      if (text === '') return;
      e.preventDefault();
      void optiuni.laLipire?.(cursorRef.current, dinTsv(text));
    },
    [optiuni],
  );

  const proprietatiCelula = useCallback(
    (rand: number, col: number): ProprietatiCelula => {
      const esteCursor = cursor.rand === rand && cursor.col === col;
      const inInterval =
        interval !== null &&
        rand >= interval.randStart &&
        rand <= interval.randStop &&
        col >= interval.colStart &&
        col <= interval.colStop;
      return {
        ref: (el) => {
          const k = cheie(rand, col);
          if (el) celule.current.set(k, el);
          else celule.current.delete(k);
        },
        // Roving tabindex: o singura celula e in ordinea de tabulare la un moment dat.
        tabIndex: esteCursor ? 0 : -1,
        'data-cursor': esteCursor ? true : undefined,
        'data-in-interval': inInterval ? true : undefined,
        'aria-colindex': col + 1,
        onMouseDown: (ev) => {
          if (ev.button !== 0) return;
          if (ev.shiftKey) {
            setAncora((a) => a ?? { rand: cursor.rand, col: cursor.col });
            setCursor(limiteaza({ rand, col }));
          } else {
            mutaLa(rand, col);
          }
        },
      };
    },
    [cursor, interval, limiteaza, mutaLa],
  );

  // Cand se scurteaza lista (filtrare, amanare), cursorul nu are voie sa ramana in afara ei.
  useEffect(() => {
    setCursor((p) => {
      const c = limiteaza(p);
      return c.rand === p.rand && c.col === p.col ? p : c;
    });
  }, [limiteaza]);

  return {
    cursor,
    editare,
    interval,
    randuriSelectate,
    eroare,
    proprietatiGrila: { role: 'grid', onKeyDown: laTasta, onPaste: laLipire },
    proprietatiCelula,
    mutaLa,
    incepeEditarea,
    schimbaTextul,
    salveaza,
    renunta,
    comutaSelectia,
    selecteazaTot,
    golesteSelectia,
  };
}
